
import Locale from './Locale.js'

function isISODate (input) {
  return (
    'year' in input &&
    'month' in input &&
    'day' in input &&
    'hour' in input &&
    'minute' in input &&
    'second' in input &&
    'millisecond' in input &&
    'microsecond' in input &&
    'nanosecond' in input
  )
}

function isISOTime (input) {
  return isISODate(input) === false && (
    'hour' in input &&
    'minute' in input &&
    'second' in input &&
    'millisecond' in input &&
    'microsecond' in input &&
    'nanosecond' in input
  )
}

if (typeof Object.hasOwn === 'undefined') {
  /**
   * The Object.hasOwn() method returns true if the specified
   * property is a direct property of the object — even if the
   * property value is null or undefined. The method returns
   * false if the property is inherited, or has not been declared
   * at all. Unlike the in operator, this method does not check
   * for the specified property in the object's prototype chain.
   *
   * @param {object} obj The JavaScript object instance to test.
   * @param {string | symbol} property The String name or Symbol of the property to test.
   * @returns {boolean}
   */
  Object.hasOwn = function hasOwn (obj, property) {
    return Object.prototype.hasOwnProperty.call(obj, property)
  }
}

export default class DateTimeFormat {
  #options
  #original
  #locale
  #timeZoneGiven
  #timeZoneResolved
  #calendar
  #date = dateAmend
  #time = timeAmend
  #dateTime = datetimeAmend
  #zoned = zonedDateTimeAmend
  #instant = instantAmend

  static supportedLocalesOf (locales, options) {
    return Intl.DateTimeFormat.supportedLocalesOf(locales, options)
  }

  constructor (locales, options) {
    const hasOptions = typeof options !== 'undefined'
    const config = hasOptions ? Object.assign({}, options) : {}

    if (typeof locales === 'undefined') {
      locales = String(Locale.systemLocale)
    } else if (typeof locales === 'object' && !Array.isArray(locales)) {
      locales = String(locales)
    }

    const original = new Intl.DateTimeFormat(locales, config)
    const ro = original.resolvedOptions()

    if (hasOptions) {
      const clonedResolved = Object.assign({}, ro)
      for (const prop in clonedResolved) {
        if (!Object.hasOwn(config, prop)) {
          delete clonedResolved[prop]
        }
      }
      this.#options = clonedResolved
    } else {
      this.#options = config
    }

    this.#timeZoneGiven = config.timeZone ? config.timeZone : null
    this.#locale = ro.locale
    this.#original = original
    this.#timeZoneResolved = ro.timeZone
    this.#calendar = ro.calendar
  }

  #getFormatterLazy (val) {
    if (typeof val === 'function') {
      // If we get here, `val` is an "amender function". It will take the user's
      // options and transform them into suitable options to be passed into the
      // built-in (non-polyfill) Intl.DateTimeFormat constructor. These options
      // will vary depending on the Temporal type, so that's why we store separate
      // formatters in separate props on the polyfill's DateTimeFormat instances.
      // The efficiency happens because we don't create an (expensive) formatter
      // until the user calls toLocaleString for that Temporal type.
      val = new Intl.DateTimeFormat(this.#locale, val(this.#options))
    }

    return val
  }

  #extractOverrides (isoDate) {
    if (typeof isoDate === 'number' || isoDate instanceof Date) {
      return {}
    }
    // isISOTime
    if (isISOTime(isoDate)) {
      const formatter = this.#getFormatterLazy(this.#time)
      this.#time = formatter

      return {
        epochMilliseconds: new Date(1970, 1, 1, isoDate.hour, isoDate.minute, isoDate.second, isoDate.millisecond).valueOf(), // zonedDT.epochMilliseconds,
        formatter
      }
    }

    if (!isoDate.isZonedDate) {
      const calendar = isoDate.calendar.id

      if (calendar !== 'iso8601' && calendar !== this.#calendar) {
        throw new RangeError(
          `cannot format PlainDateTime with calendar ${calendar} in locale with calendar ${this.#calendar}`
        )
      }

      const formatter = this.#getFormatterLazy(this.#dateTime)
      this.#dateTime = formatter

      return {
        epochMilliseconds: isoDate.epochMilliseconds,
        formatter
      }
    }

    const calendar = isoDate.calendar.id
    if (calendar !== 'iso8601' && calendar !== this.#calendar) {
      throw new RangeError(
        `cannot format ZonedDateTime with calendar ${calendar} in locale with calendar ${this.#calendar}`
      )
    }

    const timeZone = isoDate.timeZone
    const objTimeZone = timeZone.id
    if (this.#timeZoneGiven && this.#timeZoneGiven !== objTimeZone) {
      throw new RangeError(`timeZone option ${this.#timeZoneGiven} doesn't match actual time zone ${objTimeZone}`)
    }

    const formatter = this.#getFormatterLazy(this.#zoned)
    this.#zoned = formatter

    return {
      epochMilliseconds: isoDate.epochMilliseconds,
      formatter,
      timeZone: objTimeZone
    }
  }

  format (datetime) {
    let { epochMilliseconds, formatter, timeZone } = this.#extractOverrides(datetime)

    if (epochMilliseconds && formatter) {
      formatter = adjustFormatterTimeZone(formatter, timeZone)
      return formatter.format(epochMilliseconds)
    }

    return this.#original.format(datetime)
  }

  formatToParts (datetime) {
    let { epochMilliseconds, formatter, timeZone } = this.#extractOverrides(datetime)

    if (epochMilliseconds && formatter) {
      formatter = adjustFormatterTimeZone(formatter, timeZone)
      return formatter.formatToParts(epochMilliseconds)
    }

    return this.#original.formatToParts(datetime)
  }

  resolvedOptions () {
    return this.#original.resolvedOptions()
  }

  formatRange (a, b) {
    if (isISODate(a) || isISODate(b)) {
      const {
        epochMilliseconds: aa,
        formatter: aformatter,
        timeZone: atz
      } = this.#extractOverrides(a)
      const {
        epochMilliseconds: bb,
        formatter: bformatter,
        timeZone: btz
      } = this.#extractOverrides(b)
      if (atz && btz && atz !== btz) {
        throw new RangeError('cannot format range between different time zones')
      }
      if (aa && bb && aformatter && bformatter && aformatter === bformatter) {
        const formatter = adjustFormatterTimeZone(aformatter, atz)

        return formatter.formatRange(aa, bb)
      }
    }

    return this.#original.formatRange(a, b)
  }

  formatRangeToParts (a, b) {
    if (isISODate(a) || isISOTime(a) || isISODate(b) || isISOTime(b)) {
      if (isISODate(a) !== isISODate(b)) {
        throw new TypeError('Intl.DateTimeFormat.formatRangeToParts accepts two values of the same type')
      }
      const { epochMilliseconds: aa, formatter: aformatter, timeZone: atz } = this.#extractOverrides(a)
      const { epochMilliseconds: bb, formatter: bformatter, timeZone: btz } = this.#extractOverrides(b)
      if (atz && btz && atz !== btz) {
        throw new RangeError('cannot format range between different time zones')
      }
      if (aa && bb && aformatter && bformatter && aformatter === bformatter) {
        const formatter = adjustFormatterTimeZone(aformatter, atz)
        // TODO: Remove type assertion after this method lands in TS lib types
        return formatter.formatRangeToParts(aa.epochMilliseconds, bb.epochMilliseconds)
      }
    }
    // TODO: Remove type assertion after this method lands in TS lib types
    return this.#original.formatRangeToParts(a, b)
  }
}

function hasDateOptions (options) {
  return 'year' in options || 'month' in options || 'day' in options || 'weekday' in options || 'dateStyle' in options
}

function hasTimeOptions (options) {
  return (
    'hour' in options || 'minute' in options || 'second' in options || 'timeStyle' in options || 'dayPeriod' in options
  )
}

function adjustFormatterTimeZone (formatter, timeZone) {
  if (!timeZone) return formatter
  const options = formatter.resolvedOptions()
  if (options.timeZone === timeZone) return formatter
  // Existing Intl isn't typed to accept Temporal-specific options and the lib
  // types for resolved options are less restrictive than the types for options.
  // For example, `weekday` is
  // `'long' | 'short' | 'narrow'` in options but `string` in resolved options.
  // TODO: investigate why, and file an issue against TS if it's a bug.
  if (options.dateStyle || options.timeStyle) {
    // Unfortunately, Safari's resolvedOptions include parameters that will
    // cause errors at runtime if passed along with
    // dateStyle or timeStyle options as per
    // https://tc39.es/proposal-intl-datetime-style/#table-datetimeformat-components.
    // This has been fixed in newer versions of Safari:
    // https://bugs.webkit.org/show_bug.cgi?id=231041
    delete options.weekday
    delete options.era
    delete options.year
    delete options.month
    delete options.day
    delete options.hour
    delete options.minute
    delete options.second
    delete options.timeZoneName
    delete options.hourCycle
    delete options.hour12
    delete options.dayPeriod
  }

  return new Intl.DateTimeFormat(options.locale, { ...options, timeZone })
}

function timeAmend (optionsParam) {
  let options = amend(optionsParam, {
    year: false,
    month: false,
    day: false,
    weekday: false,
    timeZoneName: false,
    dateStyle: false
  })
  if (!hasTimeOptions(options)) {
    options = Object.assign({}, options, {
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric'
    })
  }
  return options
}

function dateAmend (optionsParam) {
  let options = amend(optionsParam, {
    hour: false,
    minute: false,
    second: false,
    dayPeriod: false,
    timeZoneName: false,
    timeStyle: false
  })
  if (!hasDateOptions(options)) {
    options = Object.assign({}, options, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric'
    })
  }
  return options
}

function datetimeAmend (optionsParam) {
  let options = amend(optionsParam, { timeZoneName: false })
  if (!hasTimeOptions(options) && !hasDateOptions(options)) {
    options = Object.assign({}, options, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric'
    })
  }
  return options
}

function zonedDateTimeAmend (optionsParam) {
  let options = optionsParam
  if (!hasTimeOptions(options) && !hasDateOptions(options)) {
    options = Object.assign({}, options, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric'
    })
    // if (options.timeZoneName === undefined) options.timeZoneName = 'short'
  }
  return options
}

function instantAmend (optionsParam) {
  let options = optionsParam
  if (!hasTimeOptions(options) && !hasDateOptions(options)) {
    options = Object.assign({}, options, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric'
    })
  }
  return options
}

function amend (optionsParam = {}, amended = {}) {
  const options = Object.assign({}, optionsParam)
  for (const opt of [
    'year',
    'month',
    'day',
    'hour',
    'minute',
    'second',
    'weekday',
    'dayPeriod',
    'timeZoneName',
    'dateStyle',
    'timeStyle'
  ]) {
    options[opt] = opt in amended ? amended[opt] : options[opt]
    if (options[opt] === false || options[opt] === undefined) delete options[opt]
  }

  return options
}
